<template>
  <div>
    <v-toolbar-title>
      <v-row no-gutters justify="space-between">
        <v-col v-for="category in Object.values(ClientFileStatus)" :key="`${category}-switch`"
               class="d-flex justify-center mx-2">
          <v-switch
            v-model="selectedStatuses[category]"
            :label="`${$vuetify.lang.t(`$vuetify.map.requests.dataIterator.states.${category}`)}: ${getFilesLength(category)}`"></v-switch>
        </v-col>
      </v-row>
      <v-row no-gutters justify="space-between" v-if="type === 'video'">
        <v-col v-for="category in Object.values(ClientFilesLocalVideoInnerType)" :key="`category-${category}-switch`"
               class="d-flex justify-center mx-2">
          <v-switch
            v-model="selectedInnerTypes[category]"
            :label="`${$vuetify.lang.t(`$vuetify.map.requests.dataIterator.categories.${category}`)}: ${getFilesInnerTypeLength(category)}`"></v-switch>
        </v-col>
      </v-row>
      <v-row no-gutters justify="space-between" v-if="type === 'logs'">
        <v-col v-for="category in Object.values(ClientFilesLocalLogsInnerType)" :key="`category-${category}-switch`"
               class="d-flex justify-center mx-2">
          <v-switch
            v-model="selectedInnerTypes[category]"
            :label="`${$vuetify.lang.t(`$vuetify.map.requests.dataIterator.categories.${category}`)}: ${getFilesInnerTypeLength(category)}`"></v-switch>
        </v-col>
      </v-row>
    </v-toolbar-title>

    <v-text-field class="mb-3 mt-n5 pt-0 pb-2"
                  v-model="search"
                  append-icon="mdi-magnify"
                  single-line
                  hide-details
                  clearable
                  :clear-icon="clearIcon"
                  :label="$vuetify.lang.t('$vuetify.shared.search')"
    ></v-text-field>

    <v-data-table :items="selectedItems" :headers="headers" item-key="id" multi-sort :mobile-breakpoint="0"
                  class="elevation-5"
                  sort-by="fileTimestamp" :sort-desc="true" :search="search">
      <template v-for="item in iconedHeaders" v-slot:[`header.${item}`]="{header}">
              <span :key="item" class="d-inline-flex align-center justify-start"
                    style="vertical-align: middle; word-wrap: break-word">
                <v-icon left>{{ header.icon }}</v-icon>
                <span>{{ header.text }}</span>
              </span>
      </template>

      <template v-slot:item.fileTimestamp="{item}">
        {{ item.formattedFileTimestamp }}
      </template>

      <template v-slot:item.fileSize="{item}">
        {{ item.formattedFileSize }}
      </template>

      <template v-slot:item.status="{item}">
        {{ $vuetify.lang.t(`$vuetify.map.requests.dataIterator.states.${item.status}`) }}
      </template>

      <template v-slot:item.fileInnerType="{item}">
        {{ $vuetify.lang.t(`$vuetify.map.requests.dataIterator.categories.${item.fileInnerType}`) }}
      </template>

      <template v-slot:item.actions="{item}">
        <tooltip-basic v-if="item.status === ClientFileStatus.INCOMPLETE"
                       :text="'$vuetify.map.requests.requestFile'"
                       :icon="requestIcon"
                       :emit-state="`request-client-file`"
                       @request-client-file="requestClientFile(item)">
        </tooltip-basic>
        <tooltip-basic v-if="item.status === ClientFileStatus.SAVED && item.isFileViewable"
                       :text="'$vuetify.map.requests.showFile'"
                       :icon="showIcon"
                       :emit-state="`show-client-file`"
                       @show-client-file="showClientFile(item)"
        ></tooltip-basic>
        <tooltip-basic v-if="item.status === ClientFileStatus.SAVED"
                       :text="'$vuetify.map.requests.downloadFile'"
                       :icon="downloadIcon"
                       :emit-state="`download-client-file`"
                       @download-client-file="downloadClientFile(item)"
        ></tooltip-basic>
        <file-timeline v-if="item.history.length > 0" :history="item.history"></file-timeline>
      </template>

    </v-data-table>

    <client-file-viewer :dialog="clientFileViewerDialog" :entry="selectedEntry"
                        @close-client-file-viewer-dialog="clientFileViewerDialog = false; selectedEntry = null">
    </client-file-viewer>

  </div>

</template>

<script>
import {
  mdiBullhorn, mdiClock, mdiDelete,
  mdiDownload,
  mdiEye, mdiFile, mdiFormatListBulletedSquare, mdiShapePlus, mdiWeight
} from '@mdi/js'
import TooltipBasic from '@/components/utils/tooltipBasic'
import {
  ClientFileInnerType, ClientFilesLocalLogsInnerType,
  ClientFilesLocalVideoInnerType,
  ClientFileStatus,
  ClientFileType,
  ServerRequestType
} from '@/enum'
import backend from '@/backend'
import { ServerRequest } from '@/models/serverRequests/ServerRequest'
import ClientFileViewer from '@/components/map/serverRequests/clientFileViewer'
import showContentMixin from '@/mixins/showContentMixin'
import { prepareEnumSwitches, requestFilesUrl, stripEnumSwitches } from '@/utils/requests'
import { defineTableHeader } from '@/utils/tableManager'
import FileTimeline from '@/components/map/serverRequests/fileTimeline'
import { FileTimelineEntry } from '@/models/serverRequests/FileTimelineEntry'

export default {
  name: 'filesDataIterator',
  components: { FileTimeline, ClientFileViewer, TooltipBasic },
  mixins: [showContentMixin(false)],
  props: {
    items: Array,
    type: String
  },
  data: () => ({
    requestIcon: mdiBullhorn,
    showIcon: mdiEye,
    downloadIcon: mdiDownload,
    clearIcon: mdiDelete,
    itemsPerPage: 8,
    selectedStatuses: prepareEnumSwitches(ClientFileStatus),
    selectedInnerTypes: prepareEnumSwitches(ClientFileInnerType),
    search: '',
    showContentStorage: {},
    clientFileViewerDialog: false,
    selectedEntry: null
  }),
  computed: {
    headers () {
      return [
        defineTableHeader('$vuetify.map.requests.dataIterator.name', 'fileName', mdiFile),
        defineTableHeader('$vuetify.map.requests.dataIterator.innerType', 'fileInnerType', mdiShapePlus),
        defineTableHeader('$vuetify.map.requests.dataIterator.time', 'fileTimestamp', mdiClock),
        defineTableHeader('$vuetify.map.requests.dataIterator.size', 'fileSize', mdiWeight),
        defineTableHeader('$vuetify.map.requests.dataIterator.status', 'status', mdiFormatListBulletedSquare),
        defineTableHeader('$vuetify.map.requests.dataIterator.actions', 'actions', null, false, '')
      ]
    },
    iconedHeaders () {
      return this.headers.filter(header => header.hasOwnProperty('icon')).map(header => header.value)
    },
    allItems () {
      return this.items
    },
    selectedItems () {
      const result = []
      const allowedInnerTypes = stripEnumSwitches(this.selectedInnerTypes)
      if (allowedInnerTypes.includes('null')) {
        allowedInnerTypes[allowedInnerTypes.indexOf('null')] = null
      }

      for (const [key, value] of Object.entries(this.selectedStatuses)) {
        if (value) {
          result.push(...this.items.filter(item => item.status === key && allowedInnerTypes.includes(item.fileInnerType)))
        }
      }

      return result
    }

  },
  methods: {
    async requestClientFile (entry) {
      const data = new ServerRequest({
        requestType: ServerRequestType.FILE_UPLOAD,
        userId: entry.userId,
        fileType: entry.fileType,
        fileTimestamp: entry.fileTimestamp.getTime(),
        fileName: entry.fileName
      }).backendModel()

      try {
        await backend.sendServerRequest(data)
        entry.status = ClientFileStatus.REQUESTED

        entry.history.push(new FileTimelineEntry({
          id: `fake-${entry.id}`,
          fileId: entry.id,
          timestamp: null,
          status: ClientFileStatus.REQUESTED
        }))
      } catch (e) {
        console.error(e)
      }
    },
    async downloadClientFile (entry) {
      const result = await backend._apiGet({ url: requestFilesUrl(entry), type: 'download' })

      const url = window.URL.createObjectURL(new Blob([result.data], { type: result.headers['content-type'] }))
      const link = document.createElement('a')
      link.href = url

      link.setAttribute('download', result.headers.filename)
      link.click()
      window.URL.revokeObjectURL(url)
    },
    showClientFile (entry) {
      this.selectedEntry = entry
      this.clientFileViewerDialog = true
    },
    getFilesLength (status) {
      return this.selectedItems.filter(item => item.status === status).length
    },
    getFilesInnerTypeLength (innerType) {
      if (innerType === 'null') {
        innerType = null
      }
      return this.selectedItems.filter(item => item.fileInnerType === innerType).length
    },
    basename (filePath) {
      return filePath.split('\\').pop().split('/').pop()
    }
  },
  watch: {
    selectedItems: {
      handler (value) {
        value.forEach(item => {
          this.$set(this.showContentStorage, item.id, false)
        })
      },
      deep: true
    }
  },
  created () {
    this['ClientFileStatus'] = ClientFileStatus
    this['ClientFileType'] = ClientFileType
    this['ClientFileInnerType'] = ClientFileInnerType
    this['ClientFilesLocalVideoInnerType'] = ClientFilesLocalVideoInnerType
    this['ClientFilesLocalLogsInnerType'] = ClientFilesLocalLogsInnerType
  }
}
</script>

<style scoped>

</style>
